import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';

import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';

import { SharedModule } from '@shared/shared.module';
import { RootRoutingModule } from './root-routing.module';

import { RootComponent } from './root.component';
import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, HttpClientModule } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { RoutingInfoService } from '@shared/services/routingInfo.service';
import { LoaderInterceptor } from '@shared/interceptors/loader.interceptor';
import { LoaderService } from '@shared/components/loader/loader.service';
import { LoaderComponent } from '@shared/components/loader/loader.component';

import { RunTimeConfigService } from '@shared/services/run-time-config.service';
import { AuthAdInterceptor } from '@shared/interceptors/auth-ad.interceptor';
import { APIMInterceptor } from '@shared/interceptors/apim.interceptor';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { BDDErrorHandlerInterceptor } from '@shared/interceptors/bdd-error-handler.interceptor';
import { BDDGITErrorHandlerInterceptor } from '@shared/interceptors/bdd-git-error-handler.interceptor';
import { NgcCookieConsentModule, NgcCookieConsentConfig } from 'ngx-cookieconsent';
import { DateTimeService } from '@shared/services/date-time.service';
import { ToastService } from '@shared/modules/toast/services/toast.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { AuthConfig, OAuthModule, OAuthModuleConfig, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Idle, IdleExpiry, SimpleExpiry } from '@ng-idle/core';

declare global {
  interface Document {
    documentMode?: any;
  }
}

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function initializeApp(runTimeConfigService: RunTimeConfigService) {
  return () => runTimeConfigService.load();
}

export function oAuthConfigFactory(runTimeConfigService: RunTimeConfigService) {
  return runTimeConfigService.getOAuthConfig();
}

export function oAuthModuleConfigFactory(runTimeConfigService: RunTimeConfigService) {
  return runTimeConfigService.getOAuthModuleConfig();
}

// We need a factory since localStorage is not available at AOT build time
export function storageFactory(): OAuthStorage {
  return localStorage;
}

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

const cookieConfig: NgcCookieConsentConfig = {
  cookie: {
    domain: window.location.hostname // new URL(environment.appUrl).hostname // or 'your.domain.com' // it is mandatory to set a domain, for cookies to work properly (see https://goo.gl/S2Hy2A)
  },
  palette: {
    popup: {
      background: '#ffffff',
      text: '#2b0a3d',
      link: '#0070ad'
    },
    button: {
      background: '#ffffff',
      text: '#0070ad',
      border: '#0070ad'
    }
  },
  theme: 'classic',
  type: 'opt-out',
  layout: 'my-custom-layout',
  layouts: {
    'my-custom-layout': '{{messagelink}}{{compliance}}'
  },
  elements: {
    messagelink: `
    <span id="cookieconsent:desc" class="cc-message">{{message}}
      <a aria-label="learn more about cookies" tabindex="0" class="cc-link" href="{{cookiePolicyHref}}" target="_blank">{{cookiePolicyLink}}</a> and our
      <a aria-label="learn more about our privacy policy" tabindex="1" class="cc-link" href="{{privacyPolicyHref}}" target="_blank">{{privacyPolicyLink}}</a>
    </span>
    `
  },
  content: {
    message: 'By using our site, you acknowledge that you have read and understand our ',

    cookiePolicyLink: 'Cookie policy',
    cookiePolicyHref: '#',

    privacyPolicyLink: 'Privacy policy',
    privacyPolicyHref: '#'
  }
};

let isIE;
if (
  document.documentMode ||
  /Edge/.test(navigator.userAgent) ||
  /Edg/.test(navigator.userAgent) ||
  /Trident/.test(navigator.userAgent)
) {
  isIE = true;
}

@NgModule({
  declarations: [RootComponent, LoaderComponent],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    RootRoutingModule,
    SharedModule.forRoot(),
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      },
      isolate: false,
      extend: true
    }),
    NgIdleKeepaliveModule.forRoot(),
    NgcCookieConsentModule.forRoot(cookieConfig),
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.staging || environment.production }),
    OAuthModule.forRoot()
  ],
  exports: [ReactiveFormsModule, FormsModule],
  providers: [
    RunTimeConfigService,
    ToastService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [RunTimeConfigService, TranslateService, Injector],
      multi: true
    },
    { provide: AuthConfig, useFactory: oAuthConfigFactory, deps: [RunTimeConfigService] },
    { provide: OAuthModuleConfig, useFactory: oAuthModuleConfigFactory, deps: [RunTimeConfigService] },
    { provide: OAuthStorage, useFactory: storageFactory, deps: [RunTimeConfigService] },
    DateTimeService,
    RoutingInfoService,
    LoaderService,
    SimpleExpiry,
    { provide: IdleExpiry, useClass: SimpleExpiry },
    Idle,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthAdInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: APIMInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BDDErrorHandlerInterceptor, // todo: move to respective module
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BDDGITErrorHandlerInterceptor, // todo: move to respective module
      multi: true
    }
  ],
  bootstrap: [RootComponent]
})
export class RootModule {}
